import {
  type FC,
} from 'react';
import {
  Box,
  Stack,
  Skeleton,
  useTheme,
  Typography,
} from '@mui/joy';
import {
  alpha
} from '@mui/system';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  useToaster
} from '@shared/ui';
import {
  useConfetti
} from '@shared/lib';
import {
  ChallengeVariantEnum,
  ChallengeStatusEnum,
  type Challenge,
} from '../model';
import {
  expiredImage,
} from '../assets';
import {
  useLazyRedeemChallengeQuery
} from '../api';
import {
  ChallengeTimer
} from './challenge-timer.component';
import {
  ChallengeProgressInfo
} from './challenge-progress-info.component';
import {
  ChallengeButton
} from './challenge-button.component';
import {
  ChallengeGiftImage
} from './challenge-gift-image.component';


dayjs.extend(utc);

export type ChallengeListItemProps = {
  loading?: boolean;
  challenge: Challenge;
  sideBarVersion?: boolean;
  onClaim?: () => void;
};

export const ChallengeListItem: FC<ChallengeListItemProps> = ({
  challenge,
  sideBarVersion = false,
  onClaim,
  loading,
}) => {
  const {
    title,
    status,
    target,
    endDate,
    progress,
    variant,
    subTitle,
    percentage,
    challengeUUID,
    successMessage,
    giftClaimedImage,
    giftUnclaimedImage,
  } = challenge;

  const theme = useTheme();
  const toaster = useToaster();
  const confetti = useConfetti();
  const [redeemChallenge] = useLazyRedeemChallengeQuery();

  const getColorsFromVariant = (variant: ChallengeVariantEnum) => {
    switch (variant) {
      case ChallengeVariantEnum.Blue:
        return [theme.palette.common[300], theme.palette.common.white, `linear-gradient(180deg, ${theme.palette.common.white} 0%, ${alpha(theme.palette.common[1350], 0)} 100%)`];
      case ChallengeVariantEnum.DarkBlue:
        return [theme.palette.common[400], theme.palette.common.white, `linear-gradient(180deg, ${theme.palette.common[325]} 0%, ${alpha(theme.palette.common[325], 0)} 100%)`];
      case ChallengeVariantEnum.Green:
        return [theme.palette.common[700], theme.palette.common.white, `linear-gradient(180deg, ${theme.palette.common[675]} 0%, ${alpha(theme.palette.common[675], 0)} 100%)`];
      case ChallengeVariantEnum.Yellow:
        return [theme.palette.common[1065], theme.palette.common[475], `linear-gradient(180deg, ${theme.palette.common.white} 0%, ${alpha(theme.palette.common[1350], 0)} 100%)`];
      case ChallengeVariantEnum.Red:
        return [theme.palette.common.error, theme.palette.common.white, `linear-gradient(180deg, ${alpha(theme.palette.common[1350], 0.8)} 0%, ${alpha(theme.palette.common[1350], 0)} 100%)`];
      default:
        return [theme.palette.common[300], theme.palette.common.white, `linear-gradient(180deg, ${theme.palette.common.white} 0%, ${alpha(theme.palette.common[1350], 0)} 100%)`];
    }
  }

  const [challengeBackgroundColor, challengeTypographyColor, challengeGradient] = getColorsFromVariant(variant);

  const isClaimed = status === ChallengeStatusEnum.Completed;
  const isReadyToClaim = percentage >= 100;
  const isExpired = dayjs().isAfter(dayjs(endDate).utc());

  const handleClaimChallenge = async (): Promise<void> => {
    if (isReadyToClaim) {
      try {
        const { data, isError } = await redeemChallenge({ challengeUUID });
        if (isError || data?.message) {
          console.error('Error claiming challenge:', data?.message);
          toaster?.error({ message: 'Failed to claim challenge', autoHideDuration: 3000 });
        } else {
          onClaim?.();
          confetti.fireCoins();
          toaster?.success({ message: successMessage, autoHideDuration: 8000 });
        }
      } catch (error) {
        const _error = error instanceof Error ? error : new Error('Something went wrong. Please try again later.');
        console.error('Error claiming challenge:', _error.message);
        toaster?.error({ message: 'Something went wrong. Please try again later.', autoHideDuration: 3000 });
      }
    }
  };

  return loading ? (
    <Skeleton
      variant='rectangular'
      sx={{
        height: 98,
        width: '100%',
        borderRadius: 8
      }}
    />
  ) : (
    <Stack
      direction='row'
      alignItems='flex-end'
      onClick={handleClaimChallenge}
      sx={({ palette }) => ({
        gap: 1,
        height: 90,
        borderRadius: 8,
        overflow: 'hidden',
        position: 'relative',
        padding: '8px 50px 0 4px',
        cursor: isReadyToClaim ? 'pointer' : 'initial',
        filter: isExpired ? 'grayscale(100%)' : 'initial',
        background: challengeBackgroundColor,
        '&::before': {
          content: '""',
          top: 0,
          left: 0,
          height: 35,
          opacity: 0.45,
          width: '100%',
          background: challengeGradient,
          position: 'absolute',
        },
        '&::after': {
          content: '""',
          inset: 0,
          zIndex: 2,
          opacity: 0.6,
          width: '100%',
          height: '100%',
          position: 'absolute',
          backgroundColor: palette.common[475],
          display: isReadyToClaim ? 'block' : 'none',
        }
      })}>
      <Stack
        alignItems='center'
        sx={({ breakpoints }) => ({
          width: 'fit-content',
          [breakpoints.down(1024)]: { width: 'fit-content' },
          [breakpoints.down(491)]: { width: 'fit-content' },
          ...(isClaimed && {
            width: 115,
            [breakpoints.down(1024)]: { width: 125 },
            [breakpoints.down(491)]: { width: 130 },
          })
        })}>
        <ChallengeGiftImage
          claimed={isClaimed}
          giftClaimedImage={giftClaimedImage}
          giftUnclaimedImage={giftUnclaimedImage}
        />
        {!isClaimed && (<ChallengeTimer endDate={endDate} />)}
      </Stack>
      <Stack
        width='100%'
        height='100%'
        justifyContent='center'>
        <Stack marginBottom={1}>
          <Typography sx={({ breakpoints }) => ({
            zIndex: 1,
            fontSize: sideBarVersion ? 14 : 16,
            lineHeight: 1.2,
            fontWeight: 600,
            color: challengeTypographyColor,
            [breakpoints.down(1024)]: { fontSize: 14 }
          })}>
            {title}
          </Typography>
          <Typography sx={({ breakpoints }) => ({
            zIndex: 1,
            fontWeight: 500,
            lineHeight: 1.2,
            fontSize: sideBarVersion ? 10 : 14,
            color: challengeTypographyColor,
            [breakpoints.down(1024)]: { fontSize: 10 }
          })}>
            {subTitle}
          </Typography>
        </Stack>
        <ChallengeProgressInfo
          challengeTarget={target}
          progress={progress}
          percentage={percentage}
          status={status}
          textColor={challengeTypographyColor}
        />
      </Stack>
      {(isReadyToClaim || isClaimed) && (
        <ChallengeButton readyToClaim={isReadyToClaim} />
      )}
      {isExpired && (
        <Box
          component='img'
          src={expiredImage.default}
          sx={{
            position: 'absolute',
            zIndex: 2,
            width: 55,
            right: -5,
            bottom: 0
          }}
        />
      )}
    </Stack >
  );
};